/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { TourDaoService } from '../tour-dao.service';
import { Tour } from 'src/app/_model/tour';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { TourDbTableDefinition } from './tour-db-table-definition';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';

@Injectable()
export class TourDbDaoService implements TourDaoService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private localStatusService: LocalStatusService,
    private tourTableDefinition: TourDbTableDefinition,
    private appParameter: AppParameterService,
  ) {}

  public async saveTours(tours: Tour[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var tourQueries: Query[] = [];
      for (const tour of tours) {
        let tourQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            this.tourTableDefinition.tableName +
            '(id, label, databag, description, status, extid, tournumber, templatetournumber, ' + // 8
            'shortname, type, startTstamp, endTstamp, plannedstarttstamp, plannedendtstamp,' + // 6
            'tourdate, stops, employeeid, employeelabel, carid, carlabel)' + // 6
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(tour.isLocal() ? tour.id.secondaryId : tour.id.imsId),
            tour.label,
            tour.databag ? JSON.stringify(tour.databag) : undefined,
            tour.description,
            tour.status,
            tour.extId,
            tour.tournumber,
            tour.templatetournumber,
            tour.shortname,
            tour.type,
            tour.startTstamp,
            tour.endTstamp,
            tour.plannedstarttstamp,
            tour.plannedendtstamp,
            tour.tourdate,
            tour.stops,
            tour.employee ? Number(tour.employee.getImsId()) : undefined,
            tour.employee ? tour.employee.label : undefined,
            tour.car ? Number(tour.car.getImsId()) : undefined,
            tour.car ? tour.car.label : undefined,
          ],
        };
        await this.localStatusService.storeLocalTourStatus(tour);
        tourQueries.push(tourQuery);
      }
      const result = await this.dbPersistenceService.executeBatch(
        db,
        tourQueries,
      );
    } catch (error) {
      this.logger.log('Error saving tours to db', error, '-', error.message);
    }
  }

  public async getTours(): Promise<Tour[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let tours: Tour[] = [];

    try {
      // Get Synced Tours
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.tourTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        let localStatus = await this.localStatusService.loadLocalTourStatus({
          imsId: row['id'],
          secondaryId: undefined,
          objRef: 'ims/Tour',
        });
        console.log('JBgetTours localStatus', localStatus);
        tours.push(
          Tour.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              description: row['description'],
              status: row['status'],
              extid: row['extid'],
              tournumber: row['tournumber'],
              templatetournumber: row['templatetournumber'],
              shortname: row['shortname'],
              type: row['type'],
              startTstamp: row['startTstamp'],
              endTstamp: row['endTstamp'],
              plannedstarttstamp: row['plannedstarttstamp'],
              plannedendtstamp: row['plannedendtstamp'],
              tourdate: row['tourdate'],
              stops: row['stops'],
              employee: {
                id: row['employeeid'],
                label: row['employeelabel'],
              },
              car: {
                id: row['carid'],
                label: row['carlabel'],
              },
            },
            localStatus,
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/Tour'),
          ),
        );
      }
    } catch (error) {
      this.logger.log('Error reading tour from db', error);
    }

    return tours;
  }

  public async reset() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.tourTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.tourTableDefinition.tableName,
    );
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.tourTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.tourTableDefinition.tableName,
    );
  }
}
